import { useState, useEffect, useCallback } from 'react';
import { 
  Receipt, Search, Download, Loader2, RefreshCw, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import MainLayout from '@/components/layout/MainLayout';

const formatNumber = (num) => new Intl.NumberFormat('es-ES').format(num || 0);
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-ES', { 
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
  });
};

const TRANSACTION_COLORS = {
  subscription: 'text-green-400 bg-green-500/10',
  credit_pack: 'text-green-400 bg-green-500/10',
  admin_grant: 'text-green-400 bg-green-500/10',
  generation: 'text-red-400 bg-red-500/10',
  consume: 'text-red-400 bg-red-500/10',
  refund: 'text-blue-400 bg-blue-500/10'
};

const TYPE_LABELS = {
  subscription: 'Suscripción',
  credit_pack: 'Pack créditos',
  admin_grant: 'Admin',
  generation: 'Generación',
  consume: 'Consumo',
  refund: 'Reembolso'
};

const AdminTransacciones = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: transactionsPage.toString(), limit: '20' });
      if (typeFilter !== 'all') params.append('type', typeFilter);
      
      const res = await api.get(`/admin/transactions?${params}`);
      setTransactions(res.data.transactions || []);
      setTransactionsTotal(res.data.total || 0);
    } catch (err) {
      toast.error('Error cargando transacciones');
    } finally {
      setLoading(false);
    }
  }, [api, transactionsPage, typeFilter]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleExportCSV = () => {
    if (transactions.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }
    
    const headers = ['Usuario', 'Email', 'Tipo', 'Cantidad', 'Saldo Anterior', 'Saldo Nuevo', 'Fecha', 'Descripción'];
    const rows = transactions.map(tx => [
      tx.user_name || '-',
      tx.user_email || '-',
      TYPE_LABELS[tx.type] || tx.type,
      tx.amount,
      tx.balance_before,
      tx.balance_after,
      formatDate(tx.created_at),
      tx.description || '-'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transacciones_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exportado');
  };

  const totalPages = Math.ceil(transactionsTotal / 20);

  // Filter transactions by search
  const filteredTransactions = searchTerm 
    ? transactions.filter(tx => 
        (tx.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tx.user_email || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : transactions;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="admin-transacciones-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-text-main flex items-center gap-3">
              <Receipt className="w-7 h-7 text-brand" />
              Transacciones
            </h1>
            <p className="text-text-muted mt-1">{formatNumber(transactionsTotal)} transacciones registradas</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleExportCSV} variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
            <Button onClick={fetchTransactions} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por usuario..."
              className="pl-10 bg-surface border-border-subtle"
              data-testid="transaction-search-input"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-10 px-3 rounded-lg bg-surface border border-border-subtle text-text-main text-sm"
            data-testid="type-filter"
          >
            <option value="all">Todos los tipos</option>
            <option value="subscription">Suscripción</option>
            <option value="credit_pack">Pack créditos</option>
            <option value="admin_grant">Admin</option>
            <option value="generation">Generación</option>
            <option value="refund">Reembolso</option>
          </select>
        </div>

        {/* Transactions Table */}
        <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-app-bg/50">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Usuario</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Tipo</th>
                <th className="text-right p-4 text-xs font-medium text-text-muted">Cantidad</th>
                <th className="text-right p-4 text-xs font-medium text-text-muted">Saldo Ant.</th>
                <th className="text-right p-4 text-xs font-medium text-text-muted">Saldo Nuevo</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Fecha</th>
                <th className="text-left p-4 text-xs font-medium text-text-muted">Descripción</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-brand" />
                  </td>
                </tr>
              ) : filteredTransactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-text-muted">
                    No se encontraron transacciones
                  </td>
                </tr>
              ) : (
                filteredTransactions.map((tx) => (
                  <tr key={tx.id || `tx-${tx.user_id}-${tx.created_at}`} className="border-t border-border-subtle hover:bg-surface-hover">
                    <td className="p-4">
                      <p className="text-sm text-text-main">{tx.user_name || '-'}</p>
                      <p className="text-xs text-text-muted">{tx.user_email}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${TRANSACTION_COLORS[tx.type] || 'text-gray-400 bg-gray-500/10'}`}>
                        {TYPE_LABELS[tx.type] || tx.type}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <span className={`font-medium ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {tx.amount > 0 ? '+' : ''}{formatNumber(tx.amount)}
                      </span>
                    </td>
                    <td className="p-4 text-right text-sm text-text-muted">
                      {formatNumber(tx.balance_before)}
                    </td>
                    <td className="p-4 text-right text-sm text-text-main font-medium">
                      {formatNumber(tx.balance_after)}
                    </td>
                    <td className="p-4 text-sm text-text-muted">
                      {formatDate(tx.created_at)}
                    </td>
                    <td className="p-4 text-sm text-text-muted max-w-xs truncate">
                      {tx.description || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-text-muted">
              Mostrando {((transactionsPage - 1) * 20) + 1}-{Math.min(transactionsPage * 20, transactionsTotal)} de {transactionsTotal}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTransactionsPage(p => Math.max(1, p - 1))}
                disabled={transactionsPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="px-3 py-1 text-sm text-text-main">
                {transactionsPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTransactionsPage(p => Math.min(totalPages, p + 1))}
                disabled={transactionsPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default AdminTransacciones;
